import { Children, cloneElement, isValidElement, useId } from 'react';
import type { ReactElement } from 'react';

interface FormFieldProps {
  /** Label text */
  label: string;
  /** HTML for attribute (links label to input) */
  htmlFor?: string;
  /** Show required asterisk */
  required?: boolean;
  /** Error message */
  error?: string;
  /** Help text below input */
  helpText?: string;
  /** The input element(s) */
  children: React.ReactNode;
  /** Additional className for wrapper */
  className?: string;
}

export default function FormField({
  label,
  htmlFor,
  required,
  error,
  helpText,
  children,
  className,
}: FormFieldProps) {
  const generatedId = useId();
  const only = Children.count(children) === 1 ? Children.only(children) : null;
  const onlyElement = only && isValidElement(only) ? (only as ReactElement<Record<string, unknown>>) : null;
  const childId = onlyElement ? (onlyElement.props.id as string | undefined) : undefined;

  /**
   * Resolve the id the <label> points at.
   *
   * `htmlFor` is optional and about 384 of the ~1,000 call sites omit it. Those rendered a visible
   * label whose `for` attribute was absent, next to a control with no id, no name and no
   * aria-label — so "Organization Name", "Timezone", "Email" and friends were announced by screen
   * readers as bare "edit text", and clicking the label focused nothing. Falling back to the
   * child's own id, then to a generated one that gets injected into the child below, means the
   * label is always bound to its control whether or not the caller wired it up.
   */
  /**
   * Only claim the label for a child that can actually own one.
   *
   * Some call sites wrap their control in a layout <div> (an input plus a unit suffix, a segmented
   * button row). Pointing `htmlFor` at that <div> is worse than leaving it unset: `for` may only
   * reference a labellable element, so the association is invalid and tooling that previously
   * inferred the relationship stops finding the control at all. Intrinsic tags are checked against
   * the labellable set; a composite component (`Input`, a custom picker) is trusted, since it
   * forwards to a real control and is the overwhelmingly common case.
   */
  const LABELLABLE = new Set(['input', 'select', 'textarea', 'button', 'meter', 'output', 'progress']);
  const canOwnLabel = onlyElement
    ? typeof onlyElement.type !== 'string' || LABELLABLE.has(onlyElement.type)
    : false;

  const controlId = htmlFor || childId || (canOwnLabel ? generatedId : undefined);

  /**
   * NOTE: an explicit `htmlFor` is always honoured, even when the immediate
   * child cannot own a label. It is tempting to suppress it -- a few call sites
   * do point it at nothing, which is an invalid association -- but suppressing
   * it breaks the far more common legitimate case: the caller wraps its control
   * in a layout <div> and points `htmlFor` at the real input NESTED inside.
   * FormField cannot tell those apart at render time (it never sees the
   * grandchild), so the caller keeps the last word. Genuinely dangling ones are
   * call-site bugs and are fixed there.
   */
  const errorId = error ? `${controlId}-error` : undefined;
  const helpId = helpText && !error ? `${controlId}-help` : undefined;
  const describedBy = errorId || helpId;

  /**
   * Associate the label, and the error/help text, with the control itself.
   *
   * Rendering the message as a loose <p> makes it visible but invisible to assistive tech: a
   * screen-reader user tabbing into a rejected field hears the label and nothing else, so the form
   * appears to fail for no reason. Since FormField wraps a single control across the whole app,
   * cloning that child fixes labelling and description everywhere at once.
   *
   * Only a lone valid element is cloned — multi-child or text children are passed through
   * untouched, because there is no unambiguous control to point the label at. An id the caller
   * already set always wins, and existing `aria-describedby` values are merged, never replaced. A
   * child that ignores these props (a custom component that does not spread onto the DOM) is
   * simply unaffected, which is no worse than the unassociated label it has today.
   */
  /**
   * `required` was a red asterisk and nothing else.
   *
   * 237 fields across 102 call sites mark themselves required through FormField
   * while the control underneath carries neither `required` nor `aria-required`
   * — only 11 set both. So a screen-reader user was never told the field was
   * mandatory: the sole signal was a `*` glyph in the label, which is colour and
   * punctuation, not information (WCAG 3.3.2).
   *
   * `aria-required` is injected rather than the HTML `required` attribute, and
   * that restraint is deliberate. Injecting `required` would switch native
   * browser validation on for 237 fields at once, blocking submits that succeed
   * today — fields filled programmatically, conditionally-required ones, later
   * steps of a wizard. That is a behavioural change needing per-form judgement,
   * not a blanket flip from a shared component. `aria-required` states the fact
   * without changing what the form does.
   *
   * A caller that already set either attribute keeps it.
   */
  const enhanced = onlyElement
    ? cloneElement(onlyElement, {
        id: childId || (canOwnLabel ? controlId : undefined),
        'aria-required':
          onlyElement.props['aria-required'] ??
          (onlyElement.props.required ? undefined : required || undefined),
        'aria-invalid': error ? true : onlyElement.props['aria-invalid'],
        'aria-describedby':
          [onlyElement.props['aria-describedby'], describedBy].filter(Boolean).join(' ') || undefined,
      })
    : children;

  return (
    <div className={className}>
      {/*
        The label carries an id as well as a `for`. A themed <select> renders a
        Listbox button over a visually hidden native select: `for` points at the
        select, which is where the value lives, and the button takes its
        accessible name from this element via aria-labelledby.
      */}
      <label
        id={controlId ? `${controlId}-label` : undefined}
        htmlFor={controlId}
        className="block text-sm font-medium text-theme-secondary mb-1"
      >
        {label}
        {required && <span className="text-red-600 dark:text-red-400 ml-1">*</span>}
      </label>
      {enhanced}
      {/*
        role="alert" so the message is announced when it APPEARS, not only when
        focus later reaches the control it describes.

        For an ordinary input the aria-describedby wiring above is usually
        enough — the browser's own validation moves focus to the field and the
        error is read out there. But a FormField can wrap something that is not
        a focusable control at all (the evacuation-zone boundary is a map you
        draw a polygon on), and then there is nothing for focus to land on: the
        operator submits, focus stays on the button, and the only feedback is red
        text several hundred pixels up the page. Silent failure, mid-evacuation.

        This does not chatter during live validation: an alert fires on content
        CHANGE, and the message text stays identical while the field stays
        invalid.
      */}
      {error && (
        <p id={errorId} role="alert" className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      {helpText && !error && (
        <p id={helpId} className="mt-1 text-sm text-theme-tertiary">
          {helpText}
        </p>
      )}
    </div>
  );
}
